import { useQueryClient } from '@tanstack/react-query'
import { HttpStatusCode } from 'axios'
import dayjs from 'dayjs'
import { useEffect, useMemo } from 'react'

import { useParkingSlots } from '@/api/models/parking/hooks/useParkingSlots'
import { GET_SUMMARY, useSummary } from '@/api/models/parking/hooks/useSummary'
import { TariffType } from '@/api/models/parking/lib/parking.types'
import { isServerValidationError } from '@/utils/parseServerErrorField'

type Props = {
  selectedTab: TariffType
  from: string
  to: string
  slotsPeriod: { from: string; to: string }
  autoNumber?: string
  isStandard: boolean
  personalAccountId?: string
  isCalendarOpen: boolean
}

export const useAutoPassAvailability = ({
  selectedTab,
  from,
  to,
  slotsPeriod,
  autoNumber,
  isStandard,
  personalAccountId,
  isCalendarOpen
}: Props) => {
  const queryClient = useQueryClient()

  useEffect(() => {
    return () => {
      // при размонтировании формы чистим кэш summary
      queryClient.removeQueries({ queryKey: [GET_SUMMARY] })
    }
  }, [queryClient])

  const {
    data: summary,
    refetch: refetchSummary,
    isError: isSummaryError,
    error: summaryError
  } = useSummary({
    type: selectedTab,
    from,
    to,
    autoNumber,
    isStandard,
    personalAccountId
  })

  const {
    data: parkingSlots,
    isLoading: isParkingSlotsLoading,
    isFetching: isParkingSlotsFetching,
    error: parkingSlotsError
  } = useParkingSlots({
    type: selectedTab,
    from: slotsPeriod.from,
    to: slotsPeriod.to,
    autoNumber,
    isStandard,
    personalAccountId,
    enabled: isCalendarOpen
  })

  const isConflictError =
    summaryError?.response?.status === HttpStatusCode.Conflict

  const conflictMessage = isConflictError
    ? summaryError?.response?.data?.title
    : undefined

  const isSummaryValidationError = isServerValidationError(summaryError)
  const isParkingSlotsValidationError =
    isServerValidationError(parkingSlotsError)

  const getAutoPassError = () => {
    if (isSummaryValidationError) {
      return summaryError
    }

    if (isParkingSlotsValidationError) {
      return parkingSlotsError
    }
    return undefined
  }

  const individualDaysToSelect = useMemo(() => {
    const getSlotDisplayEnd = (end: string) =>
      dayjs(end).subtract(1, 'second').toISOString()

    return {
      bordered: parkingSlots
        .filter(slot => slot.isMine)
        .map(slot => ({ from: slot.start, to: getSlotDisplayEnd(slot.end) })),
      disabled: parkingSlots
        .filter(slot => !slot.isMine)
        .map(slot => ({ from: slot.start, to: getSlotDisplayEnd(slot.end) }))
    }
  }, [parkingSlots])

  return {
    summary,
    refetchSummary,
    isSummaryError,
    isConflictError,
    conflictMessage,
    isSummaryValidationError,
    getAutoPassError,
    isParkingSlotsLoading,
    isParkingSlotsFetching,
    individualDaysToSelect
  }
}
